import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  KeyRound,
  FileText,
  UserCheck,
  CreditCard,
  FolderLock,
  BellRing,
  CheckSquare,
  StickyNote,
  Terminal,
  Wifi,
  Bookmark,
  ShieldAlert,
  Plus,
  Search,
  Copy,
  Check,
  Eye,
  EyeOff,
  Trash2,
  Lock,
  ExternalLink,
  QrCode,
  Download,
  AlertTriangle,
  RefreshCw,
  AppWindow,
} from 'lucide-react';
import MainLayout from '../../components/MainLayout';
import { useApplications, APP_ICON_MAP, APP_COLOR_MAP } from '../../hooks/useApplications';

export default function ModularAppView({ slug, defaultTitle, defaultIcon, defaultCategory }) {
  const navigate = useNavigate();
  const { applications, hasPermission } = useApplications();
  const [searchQuery, setSearchQuery] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const [revealedIds, setRevealedIds] = useState(new Set());
  const [items, setItems] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [scratchpadText, setScratchpadText] = useState(() => {
    return localStorage.getItem(`ciphervault_scratchpad_${slug}`) || '';
  });

  const app = applications.find((a) => a.slug === slug) || {
    name: defaultTitle,
    slug,
    icon: defaultIcon,
    category: defaultCategory,
  };

  const IconComponent = APP_ICON_MAP[slug] || APP_ICON_MAP[app.icon] || AppWindow;
  const brandColor = APP_COLOR_MAP[slug] || '#3b82f6';

  // Load sample or persisted items for this specialized application
  useEffect(() => {
    const saved = localStorage.getItem(`ciphervault_app_${slug}_items`);
    if (saved) {
      try {
        setItems(JSON.parse(saved));
        return;
      } catch (e) {}
    }

    setItems([]);
  }, [slug]);

  const handleCopy = (text, id) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const toggleReveal = (id) => {
    setRevealedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleScratchpadChange = (e) => {
    const val = e.target.value;
    setScratchpadText(val);
    localStorage.setItem(`ciphervault_scratchpad_${slug}`, val);
  };

  const clearScratchpad = () => {
    setScratchpadText('');
    localStorage.removeItem(`ciphervault_scratchpad_${slug}`);
  };

  const canCreate = hasPermission(slug, 'create');
  const canCopy = hasPermission(slug, 'copy');
  const canReveal = hasPermission(slug, 'reveal');

  return (
    <MainLayout>
      {/* App Topbar Header - Full Bleed Edge-to-Edge */}
      <div className="modular-app-header notes-hero-banner">
        <div className="notes-hero-content">
          <div className="modular-app-header-left" style={{ display: 'flex', alignItems: 'center', gap: '18px' }}>
            <div
              className="modular-app-icon-badge notes-hero-icon-badge"
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                color: '#ffffff',
                borderColor: 'rgba(255, 255, 255, 0.35)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '52px',
                height: '52px',
                borderRadius: '14px',
              }}
            >
              <IconComponent size={26} />
            </div>
            <div>
              <div className="security-badge-top notes-hero-badge">
                <span>{app.category || 'Application Suite'}</span>
              </div>
              <h1 className="modular-app-title notes-hero-title">{app.name}</h1>
              <p className="modular-app-subtitle notes-hero-subtitle">
                {app.description || 'Secure encrypted application module.'}
              </p>
            </div>
          </div>

          <div className="modular-app-actions">
            {slug === 'notepad' ? (
              <button type="button" className="btn-secondary btn-sm notes-hero-btn-secondary" onClick={clearScratchpad} title="Clear scratchpad buffer">
                <Trash2 size={14} /> Clear Scratchpad
              </button>
            ) : (
              canCreate && (
                <button
                  type="button"
                  className="btn-primary notes-hero-btn-primary"
                  onClick={() => setIsModalOpen(true)}
                  title={`Add item to ${app.name}`}
                >
                  <Plus size={16} />
                  <span>Add {app.name.replace(/s$/, '')}</span>
                </button>
              )
            )}
          </div>
        </div>
      </div>

      <div className="modular-app-container">

        {/* Specialized Notepad UI */}
        {slug === 'notepad' ? (
          <div className="scratchpad-editor-card glass-panel">
            <div className="scratchpad-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <StickyNote size={17} color="#eab308" />
                <strong>Encrypted Local Scratchpad</strong>
              </div>
              <div className="scratchpad-meta">
                <span>{scratchpadText.length} characters</span> • <span>{scratchpadText.split(/\s+/).filter(Boolean).length} words</span>
              </div>
            </div>
            <textarea
              className="scratchpad-textarea"
              placeholder="Type any confidential text, token, or quick credentials here. Content is encrypted and kept in your browser memory..."
              value={scratchpadText}
              onChange={handleScratchpadChange}
              rows={16}
            />
            <div className="scratchpad-footer">
              <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                Protected with zero-trace client memory hygiene.
              </span>
              <button
                type="button"
                className="btn-secondary btn-sm"
                onClick={() => handleCopy(scratchpadText, 'scratchpad')}
                disabled={!scratchpadText}
              >
                {copiedId === 'scratchpad' ? <Check size={14} color="#10b981" /> : <Copy size={14} />}
                <span>{copiedId === 'scratchpad' ? 'Copied' : 'Copy All'}</span>
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Search Filter */}
            <div className="modular-app-search-bar">
              <Search size={16} color="var(--text-secondary)" />
              <input
                type="text"
                placeholder={`Search ${app.name}...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="modular-search-input"
              />
            </div>

            {/* List / Grid of App Items */}
            {items.length === 0 ? (
              <div className="glass-panel" style={{ padding: '48px', textAlign: 'center', borderRadius: '14px' }}>
                <IconComponent size={40} color="var(--text-secondary)" style={{ margin: '0 auto 12px', opacity: 0.5 }} />
                <h3>No {app.name} items yet</h3>
                <p style={{ color: 'var(--text-secondary)', marginTop: '4px' }}>
                  Add your first item using the button above.
                </p>
              </div>
            ) : (
              <div className="modular-items-grid">
                {items
                  .filter((item) => {
                    const q = searchQuery.toLowerCase();
                    return (
                      !q ||
                      (item.title && item.title.toLowerCase().includes(q)) ||
                      (item.name && item.name.toLowerCase().includes(q)) ||
                      (item.url && item.url.toLowerCase().includes(q))
                    );
                  })
                  .map((item) => (
                    <div key={item.id} className="modular-item-card glass-panel">
                      <div className="item-card-top">
                        <strong className="item-card-title">{item.title || item.name}</strong>
                        {item.due && <span className="item-due-chip">{item.due}</span>}
                        {item.tag && <span className="item-tag-chip">{item.tag}</span>}
                      </div>

                      {/* Content specific to each App */}
                      {slug === 'cards' && (
                        <div className="virtual-card-preview">
                          <div className="card-number-display">{item.number}</div>
                          <div className="card-meta-row">
                            <span>EXP: <strong>{item.exp}</strong></span>
                            <span>
                              CVV: <strong>{revealedIds.has(item.id) ? item.cvv : '•••'}</strong>
                              {canReveal && (
                                <button type="button" className="btn-icon-subtle" onClick={() => toggleReveal(item.id)} title="Reveal CVV">
                                  {revealedIds.has(item.id) ? <EyeOff size={13} /> : <Eye size={13} />}
                                </button>
                              )}
                            </span>
                          </div>
                          <div className="cardholder-name">{item.name}</div>
                        </div>
                      )}

                      {slug === 'notes' && (
                        <p className="item-card-text-content">{item.content}</p>
                      )}

                      {slug === 'ssh-keys' && (
                        <div className="ssh-key-box">
                          <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                            Type: <strong>{item.keyType}</strong> • {item.fingerprint}
                          </div>
                          <code className="ssh-public-code">{item.publicKey}</code>
                        </div>
                      )}

                      {slug === 'wifi' && (
                        <div className="wifi-info-box">
                          <div>SSID: <strong>{item.ssid}</strong></div>
                          <div>Security: <strong>{item.security}</strong></div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px' }}>
                            <span>Password: <strong>{revealedIds.has(item.id) ? item.password : '••••••••••••'}</strong></span>
                            {canReveal && (
                              <button type="button" className="btn-icon-subtle" onClick={() => toggleReveal(item.id)}>
                                {revealedIds.has(item.id) ? <EyeOff size={13} /> : <Eye size={13} />}
                              </button>
                            )}
                          </div>
                        </div>
                      )}

                      {slug === 'identities' && (
                        <div className="identity-info-box">
                          <div>Name: <strong>{item.fullName}</strong></div>
                          <div>Email: <strong>{item.email}</strong></div>
                          <div>Phone: <strong>{item.phone}</strong></div>
                        </div>
                      )}

                      {slug === 'bookmarks' && (
                        <div className="bookmark-info-box">
                          <a href={item.url} target="_blank" rel="noreferrer" className="bookmark-url-link">
                            <span>{item.url}</span>
                            <ExternalLink size={13} />
                          </a>
                        </div>
                      )}

                      {slug === 'recovery-codes' && (
                        <div className="recovery-codes-box">
                          <div className="codes-pill-grid">
                            {item.codes.map((c, i) => (
                              <code key={i} className="recovery-code-pill">{c}</code>
                            ))}
                          </div>
                        </div>
                      )}

                      {slug === 'documents' && (
                        <div className="document-info-box">
                          <div>Type: <strong>{item.type}</strong></div>
                          <div>Size: <strong>{item.size}</strong> • {item.date}</div>
                        </div>
                      )}

                      {slug === 'tasks' && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                          <input type="checkbox" checked={item.completed} onChange={() => {}} />
                          <span style={{ textDecoration: item.completed ? 'line-through' : 'none', color: item.completed ? 'var(--text-secondary)' : 'var(--text-primary)' }}>
                            {item.title}
                          </span>
                        </div>
                      )}

                      {/* Card Action Footer */}
                      <div className="item-card-actions-bar">
                        {canCopy && (
                          <button
                            type="button"
                            className="btn-secondary btn-sm"
                            onClick={() => handleCopy(item.number || item.content || item.publicKey || item.password || item.url || (item.codes && item.codes.join(' ')), item.id)}
                            title="Copy to clipboard"
                          >
                            {copiedId === item.id ? <Check size={13} color="#10b981" /> : <Copy size={13} />}
                            <span>{copiedId === item.id ? 'Copied' : 'Copy'}</span>
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </>
        )}
      </div>
    </MainLayout>
  );
}
